<template>
  <div class="invite-players">
    <QRCodeComponent :options="qrFullOptions" hidden @qrReady="getQRSource" />
    <Widget
      v-model="filters.search_term"
      :title="$t('_web_tournament_invite_player_title', 'Invite players')"
      :indicators="indicators"
      class="mb-5"
      @input="onFilterChange"
    >
      <template #actionBtn>
        <div class="d-flex align-items-center justify-content-end w-100">
          <ToolIcon
            icon-name="share"
            variant="panel"
            class="h-mr-4"
            @click.native.prevent.stop="openLocalSharingMenu"
          />
          <ToolIcon
            v-if="currentTournament.info && currentTournament.info.vanity_id"
            icon-name="copy"
            :help-text="
              $t(
                '_web_copy-tournament-link-tournament-help',
                'Copy tournament invitation link to clipboard'
              )
            "
            variant="panel"
            class="h-mr-4"
            @click.stop="getInviteLink"
          />
          <ToolIcon
            icon-name="qr-code"
            :help-text="
              $t(
                '_web_qr-code-download-tournament-help',
                'Download QR-code for tournament invitation link'
              )
            "
            variant="panel"
            @click.stop="downloadQrCode"
          />
        </div>
      </template>
      <!-- <HeraFormBtn
        slot="actionBtn"
        class="btn-accent-simple h-ml-auto"
        :label="$t('_web_invite_list_copy_invite_link', 'Copy invite link')"
        @click.stop="getInviteLink"
      /> -->
    </Widget>

    <Paginated
      v-bind="{
        totalItems,
        hidePagination: !players.length,
        pageSize,
        inContainer: true,
      }"
      @change="fetchList"
    >
      <div class="col mb-5">
        <Table
          :columns="columns"
          :items="players"
          :show-subheader="true"
          :loading="isLoading"
          dummy-icon="group"
          :dummy-text="$$t('No players to show', 'players-empty')"
        >
          <template #head-item="{ column }">
            <TextLabel :text="column.label" variant="caption" />
          </template>
          <template #subhead-item="{ column }">
            <CustomFilter
              v-if="column.name === 'status'"
              v-model="filters.status"
              :options="tabsList"
              :disabled="isLoading"
              class="custom-filter"
              show-default-option-item
              @close="onFilterChange"
            />

            <HeraFormDropdown
              v-if="column.name === 'game'"
              v-model="filters.game"
              :list="gamesWithEmptyVal"
              :disabled="isLoading"
              variant="input-like"
              show-label
              empty-option
              :empty-text="emptyGamesText"
              class="custom-filter"
              @input="onFilterChange"
            >
              <template #chosen="{ chosenItem }">
                {{ chosenItem.name }}
              </template>
              <template #item="{ item }">
                <TextBlock :text="item.name" />
              </template>
            </HeraFormDropdown>

            <HeraFormDropdown
              v-if="column.name === 'platform'"
              v-model="filters.platform"
              :list="platformsWithEmptyVal"
              :disabled="isLoading"
              variant="input-like"
              show-label
              empty-option
              :empty-text="emptyPlatformsText"
              class="custom-filter"
              @input="onFilterChange"
            >
              <template #chosen="{ chosenItem }">
                {{ chosenItem.name }}
              </template>
              <template #item="{ item }">
                <TextBlock :text="item.name" />
              </template>
            </HeraFormDropdown>
          </template>
          <template #body-item="{ item: player, column }">
            <ListItemAccount
              v-if="column.name === 'username'"
              v-bind="{
                type: isSingle ? 'profile' : 'clan',
                name: $filters.displayName(player),
                avatar: player.avatar,
                accountId: player.id,
                size: 36,
              }"
              class="account"
            />
            <TextBlock
              v-if="column.name === 'status'"
              :text="getStatusLabel(player.status)"
            />
            <Avatar
              v-if="
                column.name === 'platform' &&
                  filters.platform &&
                  filters.platform.logo
              "
              :img-url="filters.platform.logo.uri"
              :alt="filters.game.name"
              :size="36"
              type="platform"
            />
            <Avatar
              v-if="column.name === 'game' && filters.game && filters.game.logo"
              :img-url="filters.game.logo"
              :alt="filters.game.name"
              :size="36"
              type="game"
            />
            <Icon
              v-if="column.name === 'chat'"
              class="cursor-pointer"
              name="chat"
              width="18"
              :fill="$scss.secondaryTextBg"
              @click.stop="startChat(player)"
            />
            <template v-if="column.name === 'button'">
              <HeraFormBtn
                v-if="player.status === 'None'"
                :fixed-width="120"
                class="btn-accent-simple w-100"
                :class="{ 'is-loading': loadPlayerStatus === player }"
                :label="$t('_web_invite_list_invite', 'Invite')"
                @click.capture="onInvite(player)"
              />

              <HeraFormBtn
                v-else-if="player.status === 'Invited'"
                :fixed-width="120"
                class="btn-secondary-simple w-100"
                :class="{ 'is-loading': loadPlayerStatus === player }"
                :label="$t('_web_invite_list_cancel-invite', 'Invited')"
                @click="onCancelInvite(player)"
              />
            </template>
          </template>
        </Table>
      </div>
    </Paginated>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import listMixin from '@/mixins/paginatedLists';
import shareQrCodeMixin from '@/mixins/shareQrCode';
import { sharingMenu } from '@/mixins/sharingMenu';
import { translate } from '@/mixins/translate';
import CustomFilter from '@/components/form/MultiSelectBox';
import HeraFormDropdown from '@/components/molecules/form/HeraFormDropdown';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import Icon from '@/components/atoms/Icon';
import TextBlock from '@/components/atoms/TextBlock';
import TextLabel from '@/components/atoms/TextLabel';
import ListItemAccount from '@/components/common/ListItemAccount';
import Avatar from '@/components/images/Avatar';
import Table from '@/components/molecules/Table';
import Widget from '@/views/tournaments/management/summaryWidgets/Widget';
import Paginated from '@/components/common/Paginated';
import { shareLink } from '@/mixins/sharingLink';
import { TOURNAMENT } from '@/constants/accounts';
import QRCodeComponent from '@/components/common/QRCode';
import ToolIcon from '@/views/tournaments/management/brackets/components/ToolIcon';
export default {
  name: 'InviteList',

  components: {
    Paginated,
    CustomFilter,
    HeraFormBtn,
    TextBlock,
    ListItemAccount,
    Icon,
    Avatar,
    Widget,
    Table,
    TextLabel,
    HeraFormDropdown,
    QRCodeComponent,
    ToolIcon,
  },

  mixins: [
    ...listMixin,
    translate('_web_invite_list'),
    shareLink,
    ...shareQrCodeMixin,
    sharingMenu,
  ],
  props: {
    tab: {
      type: String,
      default: 'all',
    },
  },
  data() {
    return {
      loadPlayerStatus: null,
      players: [],
      invited: [],
      filters: {},
      isLoading: true,
      emptyGamesText: this.$$t('All', 'all-games'),
      emptyPlatformsText: this.$$t('All', 'all-platforms'),
      columns: [
        {
          name: 'username',
          label: this.$$t('Username'),
          _thClass: 'username-column',
        },
        { name: 'status', label: this.$$t('Status') },
        { name: 'game', label: this.$$t('Game') },
        { name: 'platform', label: this.$$t('Platform') },
        { name: 'chat', label: this.$$t('Chat'), _thClass: 'chat-column' },
        { name: 'button' },
      ],
      tabsList: [
        {
          id: 'invited',
          name: this.$t('_web_tournaments-invites_invited-players', 'Invited'),
        },
        {
          id: 'joined',
          name: this.$t('_web_tournaments-invites_joined-players', 'Joined'),
        },
        {
          id: 'none',
          name: this.$t(
            '_web_tournaments-invites_not-invited-players',
            'Not Invited'
          ),
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'currentTournament',
      'platforms',
      'games',
      'genders',
      'isSingle',
    ]),
    indicators() {
      const playersInfo =
        this.$lodash.get(this, 'currentTournament.players_info') || {};
      return [
        /* {
          label: this.$$t('Invited'),
          // no data
          limit: 12,
          current: 2,
        }, */
        {
          label: this.$$t('Registered'),
          current: playersInfo.current_total,
          limit: this.currentTournament.players_config.squads,
          fraction: true,
        },
        {
          label: this.$$t('Reserved'),
          limit: playersInfo.current_total,
          current: playersInfo.current_reserves,
        },
        {
          label: this.$$t('Qualified'),
          limit: this.currentTournament.players_config.squads,
          current: playersInfo.current_qualifiers,
        },
        {
          label: this.$$t('Checked-in'),
          current: playersInfo.current_checked_in,
        },
      ];
    },
    tournamentID() {
      return this.$route.params.tournamentID;
    },
    gamesWithEmptyVal() {
      return [{ name: this.emptyGamesText }, ...this.games];
    },
    platformsWithEmptyVal() {
      return [{ name: this.emptyPlatformsText }, ...this.platforms];
    },
  },
  watch: {
    'currentTournament.info': function() {
      this.setQrPayload();
    },
  },
  created() {
    if (this.currentTournament && this.currentTournament.info) {
      this.setQrPayload();
    }
    this.fetchList();
  },
  methods: {
    ...mapActions([
      'fetchInviteListPlayers',
      'sendPrivateInvite',
      'cancelPrivateInvite',
      'errorNotify',
      'startTournamentChat',
    ]),

    setQrPayload() {
      this.qrPayload = this.generateInviteLink();
    },
    generateInviteLink() {
      return this.getSharingLink({
        type: TOURNAMENT,
        id: this.tournamentID,
        vanityId: this.currentTournament.vanity_id,
        token: this.currentTournament.info.registration_token,
      });
    },
    getInviteLink() {
      this.copyLinkToClipboard(this.generateInviteLink());
    },

    openLocalSharingMenu(event) {
      this.openSharingMenu({
        event,
        type: TOURNAMENT,
        text: `${this.currentTournament.info.title} ${this.$t(
          '_web_' + TOURNAMENT,
          TOURNAMENT
        )} on `,
        id: this.currentTournament.id,
        vanityId: this.currentTournament.vanityId,
        skipItems: ['copy-link', 'repost'],
      });
    },

    startChat(player) {
      this.startTournamentChat(player).catch(this.errorNotify);
    },

    getStatusLabel(status) {
      //statuses: 'None' 'Invited' 'Joined'
      return status === 'None' ? this.$$t('Not invited') : this.$$t('Invited');
    },

    onInvite(player) {
      this.loadPlayerStatus = player;
      this.sendPrivateInvite({ ...player, tournamentID: this.tournamentID })
        .then(() => (player.status = 'Invited'))
        .catch(this.errorNotify)
        .finally(() => {
          this.loadPlayerStatus = null;
        });
    },

    onCancelInvite(player) {
      this.loadPlayerStatus = player;
      this.cancelPrivateInvite({ ...player, tournamentID: this.tournamentID })
        .then(() => (player.status = 'None'))
        .catch(this.errorNotify)
        .finally(() => {
          this.loadPlayerStatus = null;
        });
    },

    clearPlayersState() {
      this.pageIndex = 0;
      this.players = [];
      this.activeRow = null;
    },
    onFilterChange() {
      this.clearPlayersState();
      this.fetchList(this.tab);
    },
    // getInviteLink() {
    //   const linkText = this.getSharingLink({
    //     type: TOURNAMENT,
    //     id: this.tournamentID,
    //     vanityId: this.currentTournament.vanity_id,
    //     token: this.currentTournament.info.registration_token,
    //   });
    //   this.copyLinkToClipboard(linkText);
    // },
    fetchList(pageNumber) {
      this.pageIndex = pageNumber;
      return this.getPageData(
        'players',
        this.fetchInviteListPlayers({
          id: this.tournamentID,
          query: this.paramsWithPageNumber({
            ...this.filters,
            platform: this.$lodash.get(this, 'filters.platform.id')
              ? this.filters.platform
              : null,
            game: this.$lodash.get(this, 'filters.game.id')
              ? this.filters.game
              : null,
            status: this.filters.status || this.filters.search_term,
          }),
        })
      );
    },
  },
};
</script>

<style lang="scss">
.invite-players {
  .hera-btn {
    min-width: 100px;
  }
  .dropdown-popover {
    min-width: 10rem !important;
  }
  .hera-table {
    .custom-filter {
      width: 8rem;
    }
    th {
      &.username-column {
        max-width: 230px;
      }
      &.chat-column {
        max-width: 40px;
      }
    }
    tbody {
      // td:first-child {
      //   // width: 15%;
      //   // .account {
      //   //   max-width: 13rem;
      //   // }
      // }
      td:last-child {
        width: 1rem;
      }
    }
  }
}
</style>
